import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {RefrigeratorUnit} from '../models/refrigerator/refrigeratorUnit.model';
import {RefrigeratorComponent} from '../models/refrigerator/refrigeratorComponent.model';
import {JobRefrigeratorUnit} from '../models/refrigerator/jobRefrigeratorUnit.model';
import {map} from 'rxjs/operators';
import {JobRefrigerator} from '../models/refrigerator/jobRefrigerator.model';
import {Refrigerator} from '../actions/refrigerator.actions';
import UpdateJobRefrigeratorUnits = Refrigerator.UpdateJobRefrigeratorUnits;

@Injectable({
  providedIn: 'root'
})
export class RefrigeratorService {

  constructor(private http: HttpClient) {
  }

  private REFRIGERATOR_URL: string = environment.BASE_URL + 'refrigerator';
  private JOB_REFRIGERATOR_URL: string = environment.BASE_URL + 'job';

  /**
   * GETS
   */
  public getRefrigeratorUnits(): Observable<Array<RefrigeratorUnit>> {
    return this.http.get<{
      refrigeratorUnits: Array<RefrigeratorUnit>
    }>(`${this.REFRIGERATOR_URL}`).pipe(map(value => value.refrigeratorUnits));
  }

  public getRefrigeratorUnit(id: string): Observable<RefrigeratorUnit> {
    return this.http.get<RefrigeratorUnit>(`${this.REFRIGERATOR_URL}/${id}`).pipe(
    );
  }

  public getRefrigeratorComponents(): Observable<Array<RefrigeratorComponent>> {
    return this.http.get<{ components: Array<RefrigeratorComponent> }>(`${this.REFRIGERATOR_URL}/component`).pipe(map(res => res.components));
  }

  public getRefrigeratorComponent(id: string): Observable<RefrigeratorComponent> {
    return this.http.get<RefrigeratorComponent>(`${this.REFRIGERATOR_URL}/component/${id}`).pipe(
    );
  }

  public getJobRefrigeratorUnits(id: string): Observable<JobRefrigerator> {
    return this.http.get<JobRefrigerator>(`${this.JOB_REFRIGERATOR_URL}/${id}/refrigerator`).pipe(
    );
  }

  public getJobRefrigeratorUnit(id: string, unitId: string): Observable<JobRefrigeratorUnit> {
    return this.http.get<JobRefrigeratorUnit>(`${this.JOB_REFRIGERATOR_URL}/${id}/refrigerator/${unitId}`).pipe(
    );
  }

  /**
   * CREATES
   */
  public createRefrigeratorUnits(unit: RefrigeratorUnit): Observable<RefrigeratorUnit> {
    const createUnitRequest = {
      ...unit,
      optionalComponents: unit.optionalComponents.map(component => component.id),
    };
    return this.http.post<RefrigeratorUnit>(`${this.REFRIGERATOR_URL}`, createUnitRequest).pipe();
  }

  public createRefrigeratorComponent(component: RefrigeratorComponent): Observable<RefrigeratorComponent> {
    return this.http.post<RefrigeratorComponent>(`${this.REFRIGERATOR_URL}/component`, component).pipe();
  }

  /**
   * UPDATES
   */
  public updateRefrigeratorUnits(unit: RefrigeratorUnit): Observable<RefrigeratorUnit> {
    const updateUnitRequest = {
      ...unit,
      optionalComponents: unit.optionalComponents.map(component => component.id),
    };
    return this.http.patch<RefrigeratorUnit>(`${this.REFRIGERATOR_URL}/${unit.id}`, updateUnitRequest).pipe();
  }

  public updateRefrigeratorComponent(component: RefrigeratorComponent): Observable<RefrigeratorComponent> {
    return this.http.patch<RefrigeratorComponent>(`${this.REFRIGERATOR_URL}/component/${component.id}`, component).pipe();
  }

  public updateJobRefrigeratorUnits(req: UpdateJobRefrigeratorUnits): Observable<JobRefrigerator> {
    return this.http.post<JobRefrigerator>(`${this.JOB_REFRIGERATOR_URL}/${req.id}/refrigerator`, {
      designConditions: req.designConditions?.replace(new RegExp('<br>', 'g'), '<br/>'),
      units: req.jobUnits,
      offer: req.offer?.replace(new RegExp('<br>', 'g'), '<br/>')
    });
  }

  public assignSerialNumbers(id: string): Observable<JobRefrigerator> {
    return this.http.put<JobRefrigerator>(`${this.JOB_REFRIGERATOR_URL}/${id}/refrigerator`, {});
  }

  public resetQuotePricing(id: string): Observable<JobRefrigerator> {
    return this.http.put<JobRefrigerator>(`${this.JOB_REFRIGERATOR_URL}/${id}/refrigerator/reset`, {});
  }

  /**
   * DELETE
   */
  public deleteRefrigeratorUnits(unitId: string): Observable<RefrigeratorUnit> {
    return this.http.delete<RefrigeratorUnit>(`${this.REFRIGERATOR_URL}/${unitId}`).pipe();
  }

  public deleteRefrigeratorComponent(componentId: string): Observable<RefrigeratorComponent> {
    return this.http.delete<RefrigeratorComponent>(`${this.REFRIGERATOR_URL}/component/${componentId}`).pipe();
  }


}
